import { Component, Input, OnInit, Pipe, PipeTransform } from '@angular/core';
import Product from 'src/app/model/product/product';
import { Lightbox } from 'ngx-lightbox';
import ProductAsset from 'src/app/model/product-asset';
import { CUSTOM_ADJUSTMENT_STICKER_PROPERTY } from 'src/app/helpers/ip-board-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import ProductHelper from 'src/app/helpers/product-helper';
import { ProductTypes } from 'src/app/model/product/product-type';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import ProductPhoneNumber from 'src/app/model/product/product-phone-number';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { FileHelper } from '../../../../../../helpers/file-helper';

@Component({
  selector: 'design-card',
  templateUrl: './design-card.component.html',
  styleUrls: ['./design-card.component.scss'],
})
export class DesignCardComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  products: Product[] = [];
  @Input()
  collaborative: boolean = false;
  @Input()
  existingProject: boolean = false;
  @Input()
  fromOldSystem: boolean = false;

  PRODUCT_TYPES = ProductTypes;

  nonIPBoardProductPhoneNumberContainers: NonIPBoardCompanyProductPhoneNumberContainer[];

  isImage = FileHelper.isImage;

  constructor(
    private projectDisplayObservable: ProjectDisplayObservableService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.processProductPhoneNumbers();
    this.addSubscription(
      this.projectDisplayObservable.getProductPhoneNumberChangedSubscribable(),
      () => this.processProductPhoneNumbers()
    );
  }

  openLightbox(product: Product): void {
    let albums = [];
    const assets = product.productAssets.filter((a) =>
      ProductHelper.isDesignAsset(a)
    );
    for (let asset of assets) {
      const src = '/api/assets/' + asset.asset.uuid;
      const caption = asset.asset.name;
      const thumb = '/api/assets/' + asset.asset.uuid;
      const album = {
        src: src,
        caption: caption,
        thumb: thumb,
      };

      albums.push(album);
    }
    this._lightbox.open(albums, 0);
  }

  isAdjustment(product: Product, type: string) {
    if (ProductHelper.isIPBoard(product)) {
      const ipBoardPart =
        type.endsWith('Right') && product.leftRightBoard
          ? product.otherSideBoardPart
          : product.boardPart;
      if (type === 'title') {
        return (
          !StringHelper.isEmpty(ipBoardPart.otherTitle) ||
          ipBoardPart.titleAdjustment != null
        );
      } else if (type === 'titleRight') {
        return (
          !StringHelper.isEmpty(ipBoardPart.otherSideOtherTitle) ||
          ipBoardPart.otherTitleAdjustment != null
        );
      } else if (type === 'phoneNumber') {
        return (
          !StringHelper.isEmpty(ipBoardPart.otherPhoneNumber) ||
          ipBoardPart.phoneNumberAdjustment != null
        );
      } else if (type === 'phoneNumberRight') {
        return (
          !StringHelper.isEmpty(ipBoardPart.otherSideOtherPhoneNumber) ||
          ipBoardPart.otherPhoneNumberAdjustment != null
        );
      } else if (type === 'sticker') {
        return this.getCustomAdjustmentStickerCheck(
          ipBoardPart,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_STICKER,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_STICKER
        );
      } else if (type === 'stickerRight') {
        return this.getCustomAdjustmentStickerCheck(
          ipBoardPart,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_STICKER,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_STICKER
        );
      } else if (type === 'phoneNumbers') {
        this.getCustomAdjustmentStickerCheck(
          ipBoardPart,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.PHONE_NUMBER_FROM_STOCK,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_PHONE_NUMBER,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_PHONE_NUMBER_FROM_STOCK,
          CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_OTHER_PHONE_NUMBER
        );
      }
    }
  }

  private getCustomAdjustmentStickerCheck(
    ipBoardPart: IPBoardPart,
    oneSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    oneSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    otherSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    otherSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY
  ): boolean {
    if (ipBoardPart == null || ipBoardPart.sign == null) {
      // this price only applies for stock signs and user has chosen a custom adjustment or sticker
      return null;
    }
    const oneSideFromStock =
      oneSideFromStockProperty == null
        ? null
        : ipBoardPart[oneSideFromStockProperty];
    const oneSideCustom: string =
      oneSideCustomProperty == null
        ? null
        : <string>ipBoardPart[oneSideCustomProperty];
    const otherSideFromStock =
      otherSideFromStockProperty == null
        ? null
        : ipBoardPart[otherSideFromStockProperty];
    const otherSideCustom: string =
      otherSideCustomProperty == null
        ? null
        : <string>ipBoardPart[otherSideCustomProperty];
    if (
      (oneSideFromStock != null || StringHelper.isEmpty(oneSideCustom)) &&
      (otherSideFromStock != null || StringHelper.isEmpty(otherSideCustom))
    ) {
      return false;
    } else if (
      oneSideFromStock == null &&
      !StringHelper.isEmpty(oneSideCustom) &&
      otherSideFromStock == null &&
      !StringHelper.isEmpty(otherSideCustom)
    ) {
      return true;
    }
  }

  private processProductPhoneNumbers(): void {
    if (this.existingProject) {
      this.processProductPhoneNumbersExistingProject();
    } else {
      this.processProductPhoneNumbersNewProject();
    }
  }

  private processProductPhoneNumbersExistingProject(): void {
    this.nonIPBoardProductPhoneNumberContainers = this.products.map((p) => {
      const toReturn: NonIPBoardCompanyProductPhoneNumberContainer = {
        companyProductPhoneNumbers: [],
      };

      const companyToProductPhoneNumbers = new Map<
        string,
        ProductPhoneNumber[]
      >();

      if (CollectionHelper.isPopulated(p.productPhoneNumbers)) {
        p.productPhoneNumbers.forEach((ppn) => {
          if (
            ppn.projectContact != null &&
            ppn.projectContact.company != null &&
            ProductHelper.isPhoneNumberValidForDisplay(ppn)
          ) {
            let companyPhoneNumbers = companyToProductPhoneNumbers.get(
              ppn.projectContact.company.regularName
            );
            if (companyPhoneNumbers == null) {
              companyPhoneNumbers = [];
              companyToProductPhoneNumbers.set(
                ppn.projectContact.company.regularName,
                companyPhoneNumbers
              );
            }
            companyPhoneNumbers.push(ppn);
          }
        });
        companyToProductPhoneNumbers.forEach(
          (productPhoneNumbers, companyName) => {
            toReturn.companyProductPhoneNumbers.push({
              companyName: companyName,
              phoneNumberDisplay: StringHelper.join(
                productPhoneNumbers.map((ppn) => ppn.phoneNumbers),
                ', '
              ),
              multiple: productPhoneNumbers.length > 1,
            });
          }
        );
      }

      return toReturn;
    });
  }

  private processProductPhoneNumbersNewProject(): void {
    this.nonIPBoardProductPhoneNumberContainers = this.products.map((p) => {
      const singleton: NonIPBoardCompanyProductPhoneNumber = {
        companyName: null,
        phoneNumberDisplay: null,
        multiple: false,
      };
      const toReturn: NonIPBoardCompanyProductPhoneNumberContainer = {
        companyProductPhoneNumbers: [singleton],
      };

      if (CollectionHelper.isPopulated(p.productPhoneNumbers)) {
        const validForDisplay = p.productPhoneNumbers.filter(
          ProductHelper.isPhoneNumberValidForDisplay
        );
        singleton.multiple = validForDisplay.length > 1;
        singleton.phoneNumberDisplay = StringHelper.join(
          validForDisplay.map((ppn) => ppn.phoneNumbers),
          ', '
        );
      }

      return toReturn;
    });
  }
}

@Pipe({
  name: 'filterProductAssets',
})
export class FilterProductAssetsPipe implements PipeTransform {
  public constructor() {}

  transform(productAssets: Array<ProductAsset>): ProductAsset[] {
    if (productAssets != null && productAssets.length > 0) {
      return productAssets.filter((a) => ProductHelper.isDesignAsset(a));
    } else {
      return [];
    }
  }
}

export interface NonIPBoardCompanyProductPhoneNumberContainer {
  companyProductPhoneNumbers: NonIPBoardCompanyProductPhoneNumber[];
}
export interface NonIPBoardCompanyProductPhoneNumber {
  phoneNumberDisplay: string;
  companyName: string;
  multiple: boolean;
}
